/** Bloodwell is a resource exchange, not combat damage/healing. */
export interface BloodwellResources { health: number; maxHealth: number; mana: number; maxMana: number }
export interface BloodwellTerms { selfHpCostPct?: number; restoreManaPct?: number }
export type BloodwellRefusal = 'invalid_resources' | 'mana_full' | 'insufficient_health';
export type BloodwellPlan = { ok: false; reason: BloodwellRefusal }
  | { ok: true; healthSpent: number; manaRestored: number };

/** Current resources may be fractional after ordinary regeneration. The
 * authorized visual lab keeps its existing free-cast/resource-cap behavior. */
export function planBloodwellExchange(p: BloodwellResources, terms: BloodwellTerms, labFreeCast = false): BloodwellPlan {
  const { health, maxHealth, mana, maxMana } = p;
  const hpRate = terms.selfHpCostPct, mpRate = terms.restoreManaPct;
  if (![health, maxHealth, mana, maxMana, hpRate, mpRate].every(value => typeof value === 'number' && Number.isFinite(value))
    || maxHealth <= 0 || maxMana <= 0 || maxHealth > Number.MAX_SAFE_INTEGER || maxMana > Number.MAX_SAFE_INTEGER
    || health < 0 || health > maxHealth || mana < 0 || mana > maxMana
    || !(hpRate! > 0 && hpRate! <= 1 && mpRate! > 0 && mpRate! <= 1)) return { ok: false, reason: 'invalid_resources' };
  const cost = Math.floor(maxHealth * hpRate!), gain = Math.floor(maxMana * mpRate!);
  if (cost <= 0 || gain <= 0) return { ok: false, reason: 'invalid_resources' };
  if (health <= 0 || (!labFreeCast && health <= cost)) return { ok: false, reason: 'insufficient_health' };
  if (!labFreeCast && mana === maxMana) return { ok: false, reason: 'mana_full' };
  return { ok: true, healthSpent: labFreeCast ? 0 : cost, manaRestored: Math.min(gain, maxMana - mana) };
}

/** Compact text retains sub-one gains. Rounding is disclosed, while the
 * resource receipt always retains its exact server numeric deltas. */
export function formatResourceAmount(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return '—';
  if (Number.isInteger(amount)) return String(amount);
  const compact = Number(amount.toPrecision(4));
  return compact === amount ? String(compact) : `≈${compact}`;
}

export function bloodwellRefusalText(reason: BloodwellRefusal | 'cast_refused'): string {
  if (reason === 'mana_full') return 'Your mana is already full.';
  if (reason === 'insufficient_health') return 'You need more health to use Bloodwell safely.';
  if (reason === 'invalid_resources') return 'Bloodwell cannot be used with your current resources.';
  return 'Bloodwell was not accepted.';
}
